/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { useNavigate } from 'react-router-dom'
import { getThemeColors, THEME_OPTIONS } from '../theme'
import Planes from './Planes'
import Header from './Header'
import Footer from './Footer'

// ── Paleta ────────────────────────────────────────────────────────────────────
let C = {
  bg: '#F7F6F2',
  white: '#fff',
  blue: '#2563EB',
  blueLight: '#EFF6FF',
  text: '#0f172a',
  muted: '#64748b',
  subtle: '#94a3b8',
  border: '#e5e7eb',
  green: '#15803d',
  greenBg: '#F0FDF4',
  greenBorder: '#86EFAC',
  amber: '#b45309',
  amberBg: '#fffbeb',
  amberBorder: '#FCD34D',
  red: '#dc2626',
  redBg: '#FEF2F2',
  redBorder: '#FECACA',
  purple: '#5b21b6',
  purpleBg: '#ede9fe',
  purpleBorder: '#C4B5FD',
  orange: '#c2410c',
  orangeBg: '#FFF7ED',
  orangeBorder: '#FED7AA',
}

const ESTADO_COLORS = {
  Nuevo:      { bg: '#EFF6FF', color: '#2563EB' },
  Revisado:   { bg: '#f3f4f6', color: '#374151' },
  Aplicado:   { bg: '#F0FDF4', color: '#15803d' },
  Entrevista: { bg: '#fef3c7', color: '#92400e' },
  Oferta:     { bg: '#ede9fe', color: '#5b21b6' },
  Descartado: { bg: '#FEF2F2', color: '#dc2626' },
  Aceptada:   { bg: '#F0FDF4', color: '#15803d' },
  Rechazada:  { bg: '#FEF2F2', color: '#dc2626' },
  Ghosted:    { bg: '#f3f4f6', color: '#64748b' },
}

const REC_COLORS = {
  APLICAR: { bg: '#F0FDF4', color: '#15803d' },
  REVISAR: { bg: '#fffbeb', color: '#b45309' },
  IGNORAR: { bg: '#FEF2F2', color: '#dc2626' },
}

const SEARCH_STATUS_KEY = 'jp_search_status'
const FIRST_SEARCH_AUTOTRIGGER_PREFIX = 'jp_first_search_autotriggered'
const SEARCH_MAX_ATTEMPTS = 12
const SEARCH_POLL_INTERVAL_MS = 10000
const SEARCH_MAX_AGE_MS = SEARCH_MAX_ATTEMPTS * SEARCH_POLL_INTERVAL_MS
const FIRST_SEARCH_WINDOW_MS = 10 * 60 * 1000

const readSearchStatus = () => {
  try {
    const raw = localStorage.getItem(SEARCH_STATUS_KEY)
    if (!raw) return { running: false, startedAt: null }
    const parsed = JSON.parse(raw)
    const startedAt = typeof parsed?.startedAt === 'number' ? parsed.startedAt : null
    const running = parsed?.running === true
    if (!running || !startedAt) return { running: false, startedAt: null }
    if (Date.now() - startedAt > SEARCH_MAX_AGE_MS) {
      localStorage.removeItem(SEARCH_STATUS_KEY)
      return { running: false, startedAt: null }
    }
    return { running: true, startedAt }
  } catch (_error) {
    localStorage.removeItem(SEARCH_STATUS_KEY)
    return { running: false, startedAt: null }
  }
}

const persistSearchStatus = (status) => {
  if (!status?.running || !status?.startedAt) {
    localStorage.removeItem(SEARCH_STATUS_KEY)
    return
  }
  localStorage.setItem(SEARCH_STATUS_KEY, JSON.stringify(status))
}

const getFirstSearchAutotriggerKey = (userId) => `${FIRST_SEARCH_AUTOTRIGGER_PREFIX}:${userId}`

const readFirstSearchAutotriggered = (userId) => {
  if (!userId) return false
  return localStorage.getItem(getFirstSearchAutotriggerKey(userId)) === '1'
}

const persistFirstSearchAutotriggered = (userId, value) => {
  if (!userId) return
  const key = getFirstSearchAutotriggerKey(userId)
  if (value) {
    localStorage.setItem(key, '1')
    return
  }
  localStorage.removeItem(key)
}

// ── Helpers seguros ───────────────────────────────────────────────────────────
const safeScore = (v) => {
  const s = v?.score_adjusted ?? v?.score
  return typeof s === 'number' && !isNaN(s) ? s : 0
}
const safeStr = (s) => (typeof s === 'string' ? s : '')
const getRoleText = (v) => safeStr(v?.title || v?.role || v?.puesto)
const getLocationText = (v) => safeStr(v?.location || v?.ubicacion || v?.ciudad || v?.state)

const summarizeMainReason = (v) => {
  const raw = safeStr(v?.justificacion).trim()
  if (raw) {
    const firstLine = raw
      .split(/\r?\n|[.;]/)
      .map(s => s.trim())
      .find(Boolean)
    if (firstLine) return firstLine.slice(0, 140)
  }
  if (Array.isArray(v?.gaps) && v.gaps.length > 0) return `Gap principal: ${safeStr(v.gaps[0]).slice(0, 120)}`
  if (Array.isArray(v?.riesgos) && v.riesgos.length > 0) return `Riesgo principal: ${safeStr(v.riesgos[0]).slice(0, 120)}`
  const rec = safeStr(v?.recomendacion).toUpperCase()
  if (rec === 'APLICAR') return 'Buen encaje general con tu perfil objetivo.'
  if (rec === 'REVISAR') return 'Encaje parcial; conviene revisar detalle antes de aplicar.'
  if (rec === 'IGNORAR') return 'Bajo encaje para tus criterios actuales.'
  return 'Sin detalle de motivo todavía.'
}

// ── Telemetría ────────────────────────────────────────────────────────────────
const trackEvent = async (userId, event, extra = {}) => {
  if (!userId) return
  try {
    await supabase.from('events').insert({ user_id: userId, event, ...extra })
  } catch (e) {
    console.warn('[track]', e)
  }
}

// ── Priority score ────────────────────────────────────────────────────────────
const computePriorityScore = (v) => {
  const score   = safeScore(v)
  const created = safeStr(v?.created_at || v?.date_posted)
  const ageDays = created ? (Date.now() - new Date(created).getTime()) / 86400000 : 999
  const recency = Math.max(0, 100 - ageDays * 10)
  const recMap  = { APLICAR: 1, REVISAR: 0.5, IGNORAR: 0 }
  const iaRec   = recMap[v?.recomendacion] ?? 0.5
  return Math.round((score * 0.6) + (recency * 0.25) + (iaRec * 100 * 0.15))
}

const getPriorityBadge = (v) => {
  const ps      = v.priority_score ?? computePriorityScore(v)
  const created = safeStr(v?.created_at || v?.date_posted)
  const ageDays = created ? (Date.now() - new Date(created).getTime()) / 86400000 : 999
  const isUrgent = safeScore(v) >= 75 && ageDays < 2
  if (isUrgent) return { label: '⚡ Aplicar hoy',    bg: C.redBg,   color: C.red,   border: C.redBorder   }
  if (ps >= 80) return { label: '🔥 Prioridad alta', bg: C.greenBg, color: C.green, border: C.greenBorder }
  if (ps >= 60) return { label: '⚡ Aplicar hoy',    bg: C.amberBg, color: C.amber, border: C.amberBorder }
  if (ps >= 40) return { label: '🕒 Revisar después',bg: '#f3f4f6', color: C.muted, border: C.border      }
  return null
}

const getIaDecision = (v) => {
  const rec = v?.recomendacion
  if (rec === 'APLICAR') return { label: '🎯 Aplicar hoy',     color: C.green, bg: C.greenBg, border: C.greenBorder }
  if (rec === 'REVISAR') return { label: '🧠 Solo monitorear', color: C.amber, bg: C.amberBg, border: C.amberBorder }
  if (rec === 'IGNORAR') return { label: '🚫 No prioritaria',  color: C.red,   bg: C.redBg,   border: C.redBorder   }
  return null
}

const getScoreInfo = (score) => {
  const s = typeof score === 'number' && !isNaN(score) ? score : 0
  if (s >= 85) return { label: 'Alto match',    color: '#15803d', bg: '#F0FDF4', tier: 'top'  }
  if (s >= 70) return { label: 'Buen match',    color: '#b45309', bg: '#fffbeb', tier: 'good' }
  if (s >= 50) return { label: 'Match parcial', color: '#2563EB', bg: '#EFF6FF', tier: 'ok'   }
  return             { label: 'Bajo match',    color: '#94a3b8', bg: '#f3f4f6', tier: 'low'  }
}

const getCardBorder = (scoreInfo, isProcesoTab, isHistorialTab, est, rec) => {
  if (scoreInfo.tier === 'top')           return { width: '3px', color: '#15803d', boxBorder: '#86EFAC' }
  if (scoreInfo.tier === 'good')          return { width: '2px', color: '#b45309', boxBorder: '#e5e7eb' }
  if (isProcesoTab || isHistorialTab)     return { width: '2px', color: est?.color || C.border, boxBorder: '#e5e7eb' }
  return                                         { width: '2px', color: rec?.color || C.border, boxBorder: '#e5e7eb' }
}

// ── Source pills ──────────────────────────────────────────────────────────────
const SOURCE_MAP = {
  linkedin:     { bg: '#EFF6FF', color: '#1d4ed8', label: 'LinkedIn'     },
  occ:          { bg: '#FFF7ED', color: '#c2410c', label: 'OCC'          },
  indeed:       { bg: '#F0FDF4', color: '#15803d', label: 'Indeed'       },
  computrabajo: { bg: '#FAF5FF', color: '#6d28d9', label: 'Computrabajo' },
  talent:       { bg: '#FDF2F8', color: '#9d174d', label: 'Talent.com'   },
  jooble:       { bg: '#FFF7ED', color: '#b45309', label: 'Jooble'       },
  jsearch:      { bg: '#f3f4f6', color: '#374151', label: 'JSearch'      },
}

const getSourceStyle = (source) => {
  if (!source) return { bg: '#f3f4f6', color: '#374151', label: '—' }
  const key = source.toLowerCase()
  for (const [k, v] of Object.entries(SOURCE_MAP)) {
    if (key.includes(k)) return v
  }
  return { bg: '#f3f4f6', color: '#374151', label: source }
}

// ── Tiempo preciso ────────────────────────────────────────────────────────────
const getTimeAgo = (dateStr) => {
  if (!dateStr) return null
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return null
  const diffMs   = Date.now() - d.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHrs  = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1)   return { label: 'Ahora',               bg: '#F0FDF4', color: '#15803d' }
  if (diffMins < 60)  return { label: `Hace ${diffMins}min`, bg: '#F0FDF4', color: '#15803d' }
  if (diffHrs  < 24)  return { label: `Hace ${diffHrs}h`,    bg: '#F0FDF4', color: '#15803d' }
  if (diffDays === 1) return { label: 'Hace 1 día',          bg: '#fffbeb', color: '#b45309' }
  if (diffDays <= 3)  return { label: `Hace ${diffDays} días`, bg: '#fffbeb', color: '#b45309' }
  return                     { label: `Hace ${diffDays} días`, bg: '#f3f4f6', color: '#64748b' }
}

// ── Follow-up badge ───────────────────────────────────────────────────────────
const getFollowUpBadge = (v) => {
  const now = Date.now()
  if ((!v.estado || v.estado === 'Nuevo') && v.created_at) {
    const d = new Date(v.created_at)
    if (!isNaN(d) && Math.floor((now - d.getTime()) / 86400000) >= 2)
      return { label: '⚠ Revisar', bg: '#fffbeb', color: '#b45309', border: '#FCD34D' }
  }
  if (v.estado === 'Aplicado' && v.applied_at) {
    const d = new Date(v.applied_at)
    if (!isNaN(d) && Math.floor((now - d.getTime()) / 86400000) >= 5)
      return { label: '⚠ Dar seguimiento', bg: '#FFF7ED', color: '#c2410c', border: '#FED7AA' }
  }
  if (v.estado === 'Entrevista' && v.updated_at) {
    const d = new Date(v.updated_at)
    if (!isNaN(d) && Math.floor((now - d.getTime()) / 86400000) >= 7)
      return { label: '⚠ Seguimiento entrevista', bg: '#FFF7ED', color: '#c2410c', border: '#FED7AA' }
  }
  return null
}

// ── Ghosted automático ────────────────────────────────────────────────────────
const shouldAutoGhost = (v) => {
  if (v.estado !== 'Aplicado') return false
  const ref = v.applied_at || v.updated_at
  if (!ref) return false
  const d = new Date(ref)
  if (isNaN(d.getTime())) return false
  return Math.floor((Date.now() - d.getTime()) / 86400000) >= 10
}

// ── Fit resumido ──────────────────────────────────────────────────────────────
const getFitSummary = (v, userKeywords = []) => {
  const haystack = `${safeStr(v?.title)} ${safeStr(v?.justificacion)}`.toLowerCase()
  const matchedKws = userKeywords.filter(kw => kw && haystack.includes(kw.toLowerCase())).slice(0, 2)
  const parts = [...matchedKws]
  const sp = v?.sector_pts || 0
  const sn = v?.seniority_pts || 0
  if (sp >= 30)      parts.push('Banca/Fintech')
  else if (sp >= 20) parts.push('Tech')
  if (sn >= 20)      parts.push('Senior/Director')
  else if (sn >= 12) parts.push('Manager')
  return parts.length > 0 ? parts.join(' · ') : null
}

// ── Keywords match ────────────────────────────────────────────────────────────
const computeKeywordsMatch = (v, userKeywords = []) => {
  if (!userKeywords?.length) return { matched: [], partial: [], missing: [] }
  const haystack = `${safeStr(v?.title)} ${safeStr(v?.justificacion)}`.toLowerCase()
  const matched = [], partial = [], missing = []
  for (const kw of userKeywords) {
    if (!kw) continue
    const k = kw.toLowerCase()
    if (haystack.includes(k)) matched.push(kw)
    else if (k.split(' ').some(w => w.length > 3 && haystack.includes(w))) partial.push(kw)
    else missing.push(kw)
  }
  return { matched, partial, missing }
}

// ── Highlight texto ───────────────────────────────────────────────────────────
const highlightText = (text, keywords = []) => {
  if (!keywords?.length || !text) return text || ''
  const escaped = keywords.filter(Boolean).map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  if (!escaped.length) return text
  const regex = new RegExp(`(${escaped.join('|')})`, 'gi')
  return text.split(regex).map((part, i) =>
    regex.test(part)
      ? <mark key={i} style={{ background: '#fef9c3', color: '#0f172a', borderRadius: '2px', padding: '0 2px' }}>{part}</mark>
      : part
  )
}

// ── Salario ───────────────────────────────────────────────────────────────────
const formatSalary = (min, max) => {
  const mn = typeof min === 'number' && min > 0 ? min : null
  const mx = typeof max === 'number' && max > 0 ? max : null
  if (!mn && !mx) return null
  if (mn && mx)   return `$${mn.toLocaleString()} – $${mx.toLocaleString()} MXN/mes`
  if (mn)         return `Desde $${mn.toLocaleString()} MXN/mes`
  return                 `Hasta $${mx.toLocaleString()} MXN/mes`
}

// ── Ranking ───────────────────────────────────────────────────────────────────
const computeRanking = (v) => {
  const score   = safeScore(v)
  const created = safeStr(v?.created_at || v?.date_posted)
  const ageDays = created ? (Date.now() - new Date(created).getTime()) / 86400000 : 999
  const recency = Math.max(0, 100 - ageDays * 10)
  return score * 0.7 + recency * 0.3
}

// ── Duplicados ────────────────────────────────────────────────────────────────
const normStr = (s = '') => safeStr(s).toLowerCase().replace(/[^a-z0-9áéíóúñ]/gi, ' ').replace(/\s+/g, ' ').trim()

const buildDuplicateMap = (vacantes) => {
  const map = {}
  for (const v of vacantes) {
    const key = `${normStr(v.title)}||${normStr(v.company)}`
    if (!map[key]) map[key] = []
    map[key].push(v)
  }
  const result = {}
  for (const group of Object.values(map)) {
    if (group.length > 1) {
      for (const v of group) {
        result[v.id] = group
          .filter(o => o.id !== v.id)
          .map(o => ({ label: getSourceStyle(o.source).label, url: o.url || null }))
          .filter((o, i, arr) => arr.findIndex(x => x.label === o.label) === i)
      }
    }
  }
  return result
}

// ── Memoria de decisiones ─────────────────────────────────────────────────────
const PENALTY = { salario: 8, seniority: 12, tech: 10, ubicacion: 6, otro: 5 }

const applyDiscardMemory = async (discarded, reason, allVacantes, updateScoreFn) => {
  const penalty = PENALTY[reason] || 5
  const similar = allVacantes.filter(v => {
    if (v.id === discarded.id) return false
    if (['Descartado', 'Aceptada', 'Rechazada', 'Ghosted'].includes(v.estado)) return false
    if (reason === 'salario') {
      const dMax = discarded.salario_max || discarded.salary_max || 0
      const vMax = v.salario_max || v.salary_max || 0
      return dMax > 0 && vMax > 0 && Math.abs(dMax - vMax) < 10000
    }
    if (reason === 'seniority') return Math.abs((v.seniority_pts || 0) - (discarded.seniority_pts || 0)) <= 3
    if (reason === 'tech')      return (v.skills_pts || 0) <= (discarded.skills_pts || 0) + 3
    if (reason === 'ubicacion') return normStr(v.location) === normStr(discarded.location)
    return false
  })
  for (const v of similar) {
    const newScore = Math.max(0, safeScore(v) - penalty)
    updateScoreFn(v.id, newScore)
    await supabase.from('jobs').update({ score_adjusted: newScore }).eq('id', v.id)
  }
}

// ── localStorage ──────────────────────────────────────────────────────────────
const LS_KEY = 'jp_filters'
const loadFilters = () => { try { return JSON.parse(localStorage.getItem(LS_KEY)) || {} } catch { return {} } }
const saveFilters = (f)  => { try { localStorage.setItem(LS_KEY, JSON.stringify(f)) } catch {} }

const getActionCount = () => parseInt(localStorage.getItem('jp_action_count') || '0')
const incActionCount = () => {
  const n = getActionCount() + 1
  localStorage.setItem('jp_action_count', n)
  return n
}

// ── Constantes ────────────────────────────────────────────────────────────────
const DISCARD_REASONS = [
  { value: 'salario',   label: 'Salario bajo'        },
  { value: 'seniority', label: 'Seniority no aplica' },
  { value: 'tech',      label: 'Stack no aplica'     },
  { value: 'ubicacion', label: 'Ubicación'            },
  { value: 'otro',      label: 'Otro'                 },
]

const EN_PROCESO_ACTIONS = {
  Aplicado:   [
    { label: 'Mover a ▾', next: null,         isDropdown: true },
    { label: 'Ghosted',    next: 'Ghosted',    style: () => ({ color: '#64748b', bg: '#f3f4f6', border: '#e5e7eb' }) },
    { label: 'Descartado', next: 'Descartado', style: () => ({ color: C.red,    bg: C.redBg,   border: C.redBorder }) },
  ],
  Entrevista: [
    { label: 'Mover a ▾', next: null,         isDropdown: true },
    { label: 'Ghosted',    next: 'Ghosted',    style: () => ({ color: '#64748b', bg: '#f3f4f6', border: '#e5e7eb' }) },
    { label: 'Descartado', next: 'Descartado', style: () => ({ color: C.red,    bg: C.redBg,   border: C.redBorder }) },
  ],
  Oferta: [
    { label: 'Aceptada ✓', next: 'Aceptada',  style: () => ({ color: C.green, bg: C.greenBg, border: C.greenBorder }) },
    { label: 'Rechazada',   next: 'Rechazada', style: () => ({ color: C.red,   bg: C.redBg,   border: C.redBorder   }) },
  ],
}

const MOVER_A_OPTIONS = {
  Aplicado:   [{ label: 'Entrevista', next: 'Entrevista' }, { label: 'Oferta', next: 'Oferta' }],
  Entrevista: [{ label: 'Oferta',     next: 'Oferta'     }],
}

const EN_PROCESO_ESTADOS = ['Aplicado', 'Entrevista', 'Oferta']
const HISTORIAL_ESTADOS  = ['Descartado', 'Aceptada', 'Rechazada', 'Ghosted']

const TABS = [
  { id: '🔥 Top'          },
  { id: '🧠 Recomendadas' },
  { id: 'En proceso'      },
  { id: 'Todas'           },
  { id: 'Historial'       },
]

// Keep these in sync with .github/workflows/scraper.yml
// - cron: "0 13 * * 1,3,5" => lunes, miercoles y viernes a las 8:00 a.m. CDMX
const SEARCH_RUN_CRON_UTC_DAYS = new Set([1, 3, 5])
const SEARCH_RUN_CRON_UTC_HOUR = 13
const SEARCH_RUN_TIMEZONE = 'America/Mexico_City'
const SEARCH_RUN_HUMAN_LABEL = 'Lunes, miércoles y viernes a las 8:00 a.m. CDMX'

const getNextSearchRun = (now = new Date()) => {
  for (let offset = 0; offset <= 14; offset++) {
    const candidate = new Date(now)
    candidate.setUTCDate(candidate.getUTCDate() + offset)
    candidate.setUTCHours(SEARCH_RUN_CRON_UTC_HOUR, 0, 0, 0)
    if (!SEARCH_RUN_CRON_UTC_DAYS.has(candidate.getUTCDay())) continue
    if (candidate.getTime() > now.getTime()) return candidate
  }
  return null
}

const formatCountdown = (target, now = new Date()) => {
  if (!target) return ''
  const diffMs = target.getTime() - now.getTime()
  if (diffMs <= 0) return 'en curso'
  const totalMinutes = Math.floor(diffMs / 60000)
  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60
  if (days > 0) return `en ${days}d ${hours}h`
  if (hours > 0) return `en ${hours}h ${minutes}m`
  return `en ${minutes}m`
}

const getSearchProgress = (searchStatus, nowTick) => {
  if (!searchStatus?.running || !searchStatus?.startedAt) return null
  const elapsedMs = Math.max(0, nowTick - searchStatus.startedAt)
  const pct = Math.min(95, Math.max(5, Math.round((elapsedMs / SEARCH_MAX_AGE_MS) * 100)))
  if (pct < 20) return { pct, phase: 'Recolectando vacantes', detail: 'Consultando fuentes y trayendo resultados iniciales.' }
  if (pct < 45) return { pct, phase: 'Quitando duplicados', detail: 'Eliminando repetidos para evitar ruido y costo innecesario.' }
  if (pct < 70) return { pct, phase: 'Filtrando ruido', detail: 'Aplicando filtros duros antes del scoring.' }
  if (pct < 90) return { pct, phase: 'Calculando match', detail: 'Priorizando vacantes con mayor relevancia.' }
  return { pct, phase: 'Guardando resultados', detail: 'Persistiendo vacantes y preparando la vista final.' }
}

function parseJsonField(value) {
  if (Array.isArray(value)) return value
  if (typeof value === 'string') { try { return JSON.parse(value) } catch { return [] } }
  return []
}

// ── JobCard ───────────────────────────────────────────────────────────────────
function JobCard({
  v, isProcesoTab, isHistorialTab, isNuevosTab,
  userKeywords, duplicateMap, expanded, setExpanded,
  discardOpen, setDiscardOpen, moverAOpen, setMoverAOpen,
  fadingOut, iaConfirmed, setIaConfirmed, priorityMode,
  updateEstado, updateScoreAdjusted, confirmDiscard, onTrack,
  flagIa, flagPriority, onAction,
}) {
  const displayScore   = safeScore(v)
  const rec            = REC_COLORS[v.recomendacion] || { bg: '#f3f4f6', color: '#374151' }
  const est            = ESTADO_COLORS[v.estado]     || ESTADO_COLORS['Nuevo']
  const src            = getSourceStyle(v.source)
  const timeAgo        = getTimeAgo(v.created_at || v.date_posted)
  const scoreInfo      = getScoreInfo(displayScore)
  const cardBorder     = getCardBorder(scoreInfo, isProcesoTab, isHistorialTab, est, rec)
  const followUp       = getFollowUpBadge(v)
  const priorityBadge  = flagPriority ? getPriorityBadge(v) : null
  const iaDecision     = flagIa ? getIaDecision(v) : null
  const fitSummary     = getFitSummary(v, userKeywords)
  const kwMatch        = computeKeywordsMatch(v, userKeywords)
  const salary         = formatSalary(v.salario_min ?? v.salary_min, v.salario_max ?? v.salary_max)
  const mainReason     = summarizeMainReason(v)
  const dupSources     = duplicateMap[v.id] || []
  const isExpanded     = expanded === v.id
  const isFading       = fadingOut.has(v.id)
  const isDiscardOpen  = discardOpen === v.id
  const isMoverAOpen   = moverAOpen === v.id
  const procesoActions = EN_PROCESO_ACTIONS[v.estado] || []
  const moverAOptions  = MOVER_A_OPTIONS[v.estado]   || []
  const appliedAgo     = v.applied_at ? getTimeAgo(v.applied_at) : null
  const iaState        = iaConfirmed[v.id]

  const matchDims = [
    { label: 'Sector',    pts: v.sector_pts    || 0, max: 40 },
    { label: 'Seniority', pts: v.seniority_pts || 0, max: 25 },
    { label: 'Skills',    pts: v.skills_pts    || 0, max: 20 },
    { label: 'Modalidad', pts: v.modalidad_pts || 0, max: 13 },
  ].filter(d => d.pts > 0)

  const minPct = matchDims.length
    ? Math.min(...matchDims.map(d => Math.round((d.pts / d.max) * 100)))
    : null

  const btnBase    = { fontSize: '11px', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', border: `0.5px solid ${C.border}` }
  const btnDefault = { ...btnBase, color: C.muted, background: '#f3f4f6' }
  const btnPrimary = { ...btnBase, color: C.green, background: C.greenBg, border: `0.5px solid ${C.greenBorder}`, fontWeight: '500' }
  const btnDanger  = { ...btnBase, color: C.red,   background: C.redBg,   border: `0.5px solid ${C.redBorder}`   }

  return (
    <div
      onClick={() => setMoverAOpen(null)}
      style={{
        background: C.white,
        border: `1px solid ${cardBorder.boxBorder}`,
        borderLeft: `${cardBorder.width} solid ${cardBorder.color}`,
        borderRadius: '14px', padding: '14px 18px', marginBottom: '10px',
        opacity: isFading ? 0 : v.estado === 'Descartado' ? 0.65 : 1,
        transition: 'opacity 0.35s ease, box-shadow 0.15s, transform 0.15s',
      }}
      onMouseEnter={e => {
        if (!isFading) {
          e.currentTarget.style.boxShadow = '0 10px 26px rgba(15,23,42,0.08)'
          e.currentTarget.style.transform = 'translateY(-1px)'
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = 'none'
        e.currentTarget.style.transform = 'translateY(0px)'
      }}
    >
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>

        {/* Score badge */}
        <div style={{ minWidth: '62px', borderRadius: '10px', background: scoreInfo.bg, padding: '8px 5px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative', border: `1px solid ${C.border}` }}>
          {scoreInfo.tier === 'top' && (
            <div style={{ position: 'absolute', top: '-7px', right: '-7px', fontSize: '13px' }}>🔥</div>
          )}
          <div style={{ fontSize: '24px', fontWeight: '700', color: scoreInfo.color, lineHeight: '0.95' }}>{displayScore}</div>
          <div style={{ fontSize: '9px', color: scoreInfo.color, marginTop: '3px', textAlign: 'center', lineHeight: '1.2', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{scoreInfo.label}</div>
          {v.score_adjusted != null && v.score_adjusted !== v.score && (
            <div style={{ fontSize: '8px', color: C.subtle, marginTop: '2px' }}>ajustado</div>
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>

          {/* Priority badge */}
          {flagPriority && (priorityMode || priorityBadge?.label.includes('Aplicar hoy')) && priorityBadge && (
            <div style={{ marginBottom: '6px' }}>
              <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '600', background: priorityBadge.bg, color: priorityBadge.color, border: `0.5px solid ${priorityBadge.border}` }}>
                {priorityBadge.label}
              </span>
            </div>
          )}

          <div style={{ fontSize: 'clamp(18px, 2.2vw, 22px)', fontWeight: '500', color: C.text, marginBottom: '2px', letterSpacing: '-0.3px', lineHeight: '1.2' }}>{safeStr(v.title) || '—'}</div>
          <div style={{ fontSize: '13px', fontWeight: '500', color: C.muted, marginBottom: '2px' }}>{safeStr(v.company) || '—'}</div>

          <div style={{ fontSize: '12px', color: C.subtle, marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
            {v.location  && <span>{v.location}</span>}
            {v.modalidad && <><span>·</span><span style={{ color: C.blue }}>{v.modalidad}</span></>}
            {salary      && <><span>·</span><span style={{ color: C.green, fontWeight: '500' }}>{salary}</span></>}
            {appliedAgo  && <><span>·</span><span>Aplicado {appliedAgo.label.toLowerCase()}</span></>}
          </div>

          {fitSummary && (
            <div style={{ fontSize: '11px', marginBottom: '8px' }}>
              💡 <span style={{ color: C.blue, fontWeight: '500' }}>{fitSummary}</span>
            </div>
          )}
          <div style={{ fontSize: '11px', color: C.muted, marginBottom: '9px', lineHeight: '1.5' }}>
            <span style={{ color: C.text, fontWeight: '600' }}>Motivo principal:</span> {mainReason}
          </div>

          {/* Pills */}
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '8px' }}>
            {v.estado  && <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '500', background: est.bg,     color: est.color     }}>{v.estado}</span>}
            {v.source  && <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '500', background: src.bg,     color: src.color     }}>{src.label}</span>}
            {timeAgo   && <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '500', background: timeAgo.bg, color: timeAgo.color }}>{timeAgo.label}</span>}
            {followUp  && <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '500', background: followUp.bg, color: followUp.color, border: `0.5px solid ${followUp.border}` }}>{followUp.label}</span>}
            {dupSources.length > 0 && (
              <span style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '11px', background: '#f3f4f6', color: C.subtle }}>
                También en:{' '}
                {dupSources.map((dup, i) => (
                  <span key={i}>
                    {i > 0 && ' · '}
                    {dup.url
                      ? <a href={dup.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.blue, textDecoration: 'underline' }}>{dup.label}</a>
                      : dup.label}
                  </span>
                ))}
              </span>
            )}
          </div>

          {/* Keywords match */}
          {(kwMatch.matched.length > 0 || kwMatch.partial.length > 0 || kwMatch.missing.length > 0) && (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {kwMatch.matched.map(kw => <span key={kw} style={{ padding: '2px 7px', borderRadius: '5px', fontSize: '10px', fontWeight: '500', background: C.greenBg, color: C.green, border: `0.5px solid ${C.greenBorder}` }}>✓ {kw}</span>)}
              {kwMatch.partial.map(kw => <span key={kw} style={{ padding: '2px 7px', borderRadius: '5px', fontSize: '10px', fontWeight: '500', background: C.amberBg, color: C.amber, border: `0.5px solid ${C.amberBorder}` }}>~ {kw}</span>)}
              {kwMatch.missing.map(kw => <span key={kw} style={{ padding: '2px 7px', borderRadius: '5px', fontSize: '10px', background: '#f3f4f6', color: C.subtle }}>✗ {kw}</span>)}
            </div>
          )}

          {/* Recomendación Narevya */}
          {flagIa && iaDecision && !iaState && (
            <div style={{ marginBottom: '10px', background: C.bg, borderRadius: '8px', border: `0.5px solid ${C.border}`, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px' }}>
                <span style={{ fontSize: '11px', fontWeight: '500', color: iaDecision.color }}>{iaDecision.label}</span>
                <button onClick={e => {
                  e.stopPropagation()
                  setIaConfirmed(p => ({ ...p, [v.id]: 'confirmed' }))
                  onTrack('ia_action', { job_id: v.id, suggestion: v.recomendacion?.toLowerCase() })
                  onAction()
                }} style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '5px', background: C.greenBg, color: C.green, border: `0.5px solid ${C.greenBorder}`, cursor: 'pointer' }}>
                  ✓ Confirmar
                </button>
                <button onClick={e => {
                  e.stopPropagation()
                  setIaConfirmed(p => ({ ...p, [v.id]: 'rejected' }))
                  onTrack('ia_action', { job_id: v.id, suggestion: 'rejected' })
                  onAction()
                }} style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '5px', background: '#f3f4f6', color: C.muted, border: `0.5px solid ${C.border}`, cursor: 'pointer' }}>
                  ✕ No aplica
                </button>
              </div>
              <div style={{ padding: '6px 12px', borderTop: `0.5px solid ${C.border}`, fontSize: '10px', color: C.subtle }}>
                Sugerencia de Narevya basada en tu perfil. Verifica antes de aplicar.
              </div>
            </div>
          )}
          {flagIa && iaState === 'confirmed' && <div style={{ fontSize: '11px', color: C.green, marginBottom: '10px' }}>✓ Recomendación de Narevya confirmada</div>}
          {flagIa && iaState === 'rejected'  && <div style={{ fontSize: '11px', color: C.subtle, marginBottom: '10px' }}>Recomendación de Narevya descartada</div>}

          {/* Acciones */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            {v.url && (
              <a href={v.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }} onClick={e => e.stopPropagation()}>
                <button style={btnDefault}>Ver vacante 🔗</button>
              </a>
            )}
            <button onClick={e => {
              e.stopPropagation()
              const opening = !isExpanded
              setExpanded(opening ? v.id : null)
              if (opening) onTrack('view_job', { job_id: v.id, score: displayScore, priority_score: v.priority_score ?? null, source: v.source })
            }} style={btnDefault}>
              {isExpanded ? 'Ocultar ▲' : 'Ver detalle ▼'}
            </button>

            {/* Selector cambiar estado */}
            <select 
              value={v.estado || 'Nuevo'} 
              onChange={e => { 
                e.stopPropagation()
                updateEstado(v.id, e.target.value)
                onAction()
              }}
              style={{ fontSize: '11px', padding: '6px 10px', borderRadius: '6px', border: `0.5px solid ${C.border}`, background: C.white, color: C.text, outline: 'none', cursor: 'pointer' }}>
              <option value="Nuevo">Nuevo</option>
              <option value="Revisado">Revisado</option>
              <option value="Aplicado">Aplicado</option>
              <option value="Entrevista">Entrevista</option>
              <option value="Oferta">Oferta</option>
              <option value="Descartado">Descartado</option>
            </select>

            {!isProcesoTab && !isHistorialTab && !EN_PROCESO_ESTADOS.includes(v.estado) && !HISTORIAL_ESTADOS.includes(v.estado) && (
              <button onClick={e => {
                e.stopPropagation()
                updateEstado(v.id, 'Aplicado')
                onTrack('apply_clicked', { job_id: v.id, score: displayScore, priority_score: v.priority_score ?? null })
                onAction()
              }} style={btnPrimary}>
                Aplicar ✓
              </button>
            )}

            {!isProcesoTab && !isHistorialTab && !HISTORIAL_ESTADOS.includes(v.estado) && (
              <button onClick={e => { e.stopPropagation(); setDiscardOpen(v.id); setMoverAOpen(null) }} style={btnDanger}>
                Descartar
              </button>
            )}

            {isProcesoTab && procesoActions.map(action => {
              if (action.isDropdown) {
                return (
                  <div key="mover" style={{ position: 'relative' }}>
                    <button onClick={e => { e.stopPropagation(); setMoverAOpen(isMoverAOpen ? null : v.id) }}
                      style={{ ...btnDefault, color: C.blue, background: C.blueLight, border: `0.5px solid #BFDBFE`, fontWeight: '500' }}>
                      {action.label}
                    </button>
                    {isMoverAOpen && moverAOptions.length > 0 && (
                      <div onClick={e => e.stopPropagation()}
                        style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, minWidth: '130px', overflow: 'hidden' }}>
                        {moverAOptions.map(opt => (
                          <div key={opt.next}
                            onClick={() => { updateEstado(v.id, opt.next); setMoverAOpen(null) }}
                            style={{ padding: '9px 14px', fontSize: '12px', color: C.text, cursor: 'pointer', borderBottom: `0.5px solid ${C.border}` }}
                            onMouseEnter={e => e.currentTarget.style.background = C.bg}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                            {opt.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              }
              const s = action.style()
              return (
                <button key={action.next} onClick={e => { e.stopPropagation(); updateEstado(v.id, action.next) }}
                  style={{ ...btnBase, color: s.color, background: s.bg, border: `0.5px solid ${s.border}`, fontWeight: '500' }}>
                  {action.label}
                </button>
              )
            })}

            {isHistorialTab && (
              <button onClick={e => { e.stopPropagation(); updateEstado(v.id, 'Nuevo', { discard_reason: null }) }} style={btnDefault}>
                Reactivar
              </button>
            )}
          </div>

          {/* Selector razón de descarte */}
          {isDiscardOpen && (
            <div onClick={e => e.stopPropagation()}
              style={{ marginTop: '10px', padding: '12px', background: C.redBg, borderRadius: '8px', border: `0.5px solid ${C.redBorder}` }}>
              <div style={{ fontSize: '11px', color: C.red, fontWeight: '500', marginBottom: '8px' }}>¿Por qué descartás esta vacante?</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {DISCARD_REASONS.map(r => (
                  <button key={r.value} onClick={() => { confirmDiscard(v.id, r.value); onAction() }}
                    style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: `0.5px solid ${C.redBorder}`, background: C.white, color: C.red, cursor: 'pointer' }}>
                    {r.label}
                  </button>
                ))}
                <button onClick={() => setDiscardOpen(null)}
                  style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: `0.5px solid ${C.border}`, background: 'none', color: C.muted, cursor: 'pointer' }}>
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detalle expandido */}
      {isExpanded && (
        <div style={{ marginTop: '12px', padding: '16px', background: C.bg, borderRadius: '10px', border: `1px solid ${C.border}`, fontSize: '13px' }}>

          {matchDims.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: C.text, marginBottom: '6px' }}>Match de perfil</div>
              <div style={{ fontSize: '11px', color: C.subtle, marginBottom: '8px' }}>Scoring por dimensión:</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                {[
                  { label: 'SECTOR', pts: v.sector_pts || 0, max: 40 },
                  { label: 'SENIORITY', pts: v.seniority_pts || 0, max: 25 },
                  { label: 'SKILLS', pts: v.skills_pts || 0, max: 20 },
                  { label: 'BRUTO', pts: (typeof v.score === 'number' ? v.score : displayScore), max: 100 },
                  { label: 'FINAL', pts: displayScore, max: 100 },
                ].map((item) => {
                  const pct = item.max > 0 ? Math.round((item.pts / item.max) * 100) : 0
                  const tone = pct >= 80
                    ? { bg: C.greenBg, border: C.green, text: '#137a38' }
                    : pct >= 60
                      ? { bg: C.amberBg, border: C.amber, text: '#965100' }
                      : pct >= 40
                        ? { bg: C.orangeBg, border: C.orange, text: '#9f3100' }
                        : { bg: C.redBg, border: C.red, text: '#b42318' }
                  return (
                    <div key={item.label} style={{ background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: '10px', padding: '10px 8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '9px', letterSpacing: '0.06em', color: C.subtle, marginBottom: '4px' }}>{item.label}</div>
                      <div style={{ fontSize: '22px', lineHeight: '1', fontWeight: '700', color: tone.text }}>{Math.round(item.pts)}</div>
                      <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>/ {item.max}</div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {v.justificacion && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: '700', color: C.text, marginBottom: '6px' }}>Justificación del score</div>
              <div style={{ background: C.blueLight, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.blue}`, borderRadius: '8px', padding: '10px 12px', color: C.text, lineHeight: '1.7', fontSize: '12px' }}>
                {v.justificacion}
              </div>
            </div>
          )}

          {(v.gaps?.length > 0 || v.riesgos?.length > 0) && (
            <div style={{ marginBottom: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
              {v.gaps?.length > 0 && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: C.text, marginBottom: '6px' }}>Gaps</div>
                  {v.gaps.map((g, i) => <div key={i} style={{ color: C.muted, marginBottom: '4px', fontSize: '12px', lineHeight: '1.6' }}>• {g}</div>)}
                </div>
              )}
              {v.riesgos?.length > 0 && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: C.text, marginBottom: '6px' }}>Riesgos</div>
                  {v.riesgos.map((r, i) => <div key={i} style={{ color: C.muted, marginBottom: '4px', fontSize: '12px', lineHeight: '1.6' }}>• {r}</div>)}
                </div>
              )}
            </div>
          )}

          {v.descripcion && (
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', fontWeight: '500', color: C.subtle, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Descripción</div>
              <div style={{ color: C.text, lineHeight: '1.65', fontSize: '12px', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {highlightText(v.descripcion, userKeywords)}
              </div>
            </div>
          )}

          {v.discard_reason && (
            <div style={{ fontSize: '11px', color: C.subtle }}>
              Descartado por: <strong style={{ color: C.red }}>{DISCARD_REASONS.find(r => r.value === v.discard_reason)?.label || v.discard_reason}</strong>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
export default function Dashboard({ session, flags = {}, theme = THEME_OPTIONS.LIGHT }) {
  C = getThemeColors(theme)
  const navigate = useNavigate()
  const loadInFlightRef = useRef(false)
  const firstSearchAutoTriggerRef = useRef(false)
  const vacantesCountRef = useRef(0)
  const searchStartCountRef = useRef(0)
  const canManualSearch = flags?.manual_daily_search === true

  const flagPriority = flags['modo_prioridad'] !== false
  const flagIa       = flags['evaluacion_ia']  !== false

  const [vacantes, setVacantes]           = useState([])
  const [estadoCounts, setEstadoCounts]   = useState(null)
  const [perfil, setPerfil]               = useState(null)
  const [userKeywords, setUserKeywords]   = useState([])
  const [userLocations, setUserLocations] = useState([])
  const [onboardingUpdatedAt, setOnboardingUpdatedAt] = useState(null)
  const [userId, setUserId]               = useState(null)
  const [loading, setLoading]             = useState(true)
  const [activeTab, setActiveTab]         = useState('🔥 Top')
  const [expanded, setExpanded]           = useState(null)
  const [showUpgrade, setShowUpgrade]     = useState(false)
  const [waitlistDone, setWaitlistDone]   = useState(false)
  const [discardOpen, setDiscardOpen]     = useState(null)
  const [fadingOut, setFadingOut]         = useState(new Set())
  const [moverAOpen, setMoverAOpen]       = useState(null)
  const [onlyHighMatch, setOnlyHighMatch] = useState(false)
  const [priorityMode, setPriorityMode]   = useState(false)
  const [iaConfirmed, setIaConfirmed]     = useState({})
  const [showOnboarding, setShowOnboarding] = useState(!localStorage.getItem('jp_onboarding_done'))
  const [feedbackDone, setFeedbackDone]   = useState(!!localStorage.getItem('jp_feedback_done'))
  const [showFeedback, setShowFeedback]   = useState(false)
  const [showPlansModal, setShowPlansModal] = useState(false)
  const [searchStatus, setSearchStatus] = useState(readSearchStatus)
  const [searchNotice, setSearchNotice] = useState('')
  const [hasAutoTriggeredFirstSearch, setHasAutoTriggeredFirstSearch] = useState(false)
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 900 : false))
  const [nowTick, setNowTick] = useState(Date.now())
  const searchRunning = searchStatus.running
  useEffect(() => {
    vacantesCountRef.current = vacantes.length
  }, [vacantes.length])
  useEffect(() => {
    persistSearchStatus(searchStatus)
  }, [searchStatus])
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const onResize = () => setIsMobile(window.innerWidth <= 900)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  useEffect(() => {
    const timer = window.setInterval(() => setNowTick(Date.now()), 60000)
    return () => window.clearInterval(timer)
  }, [])
  const saved = loadFilters()
  const [filterLoc,  setFilterLoc]  = useState(saved.location ?? '')
  const [filterRole, setFilterRole] = useState(saved.role     ?? '')

  // Incrementa contador de acciones y muestra feedback tras 5
  const handleAction = useCallback(() => {
    if (feedbackDone) return
    const count = incActionCount()
    if (count >= 5) setShowFeedback(true)
  }, [feedbackDone])

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (loadInFlightRef.current) return
    loadInFlightRef.current = true
    if (!silent) setLoading(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      setUserId(user.id)

      const { data: profileData } = await supabase
        .from('profiles').select('*').eq('id', user.id).single()
      setPerfil(profileData)

      const { data: sp } = await supabase
        .from('search_profiles').select('id, keywords, ubicacion, updated_at').eq('user_id', user.id).single()
      if (!sp) { navigate('/onboarding'); return }
      setUserKeywords(Array.isArray(sp.keywords) ? sp.keywords.filter(Boolean) : [])
      setOnboardingUpdatedAt(sp.updated_at || null)
      setUserLocations(
        safeStr(sp.ubicacion)
          .split(',')
          .map(v => v.trim())
          .filter(Boolean),
      )

      const { data: vacantesData, error } = await supabase
        .from('jobs').select('*')
        .eq('user_id', user.id)
        .order('score', { ascending: false })
        .limit(150)

      if (error) console.error('[Dashboard]', error)

      const { data: estadosData, error: estadosError } = await supabase
        .from('jobs').select('estado')
        .eq('user_id', user.id)
      if (estadosError) console.error('[Dashboard][estado-counts]', estadosError)
      const byEstado = (estadosData || []).reduce((acc, row) => {
        const key = safeStr(row?.estado) || 'Nuevo'
        acc[key] = (acc[key] || 0) + 1
        return acc
      }, {})
      setEstadoCounts(byEstado)

      const normalized = (vacantesData || []).map(v => ({
        ...v,
        score:    typeof v.score === 'number' ? v.score : 0,
        gaps:     parseJsonField(v.gaps),
        riesgos:  parseJsonField(v.riesgos),
        title:    safeStr(v.title),
        company:  safeStr(v.company),
        location: safeStr(v.location),
        source:   safeStr(v.source),
      }))

      const toGhost = normalized.filter(shouldAutoGhost)
      for (const v of toGhost) {
        await supabase.from('jobs').update({ estado: 'Ghosted' }).eq('id', v.id)
      }

      const withGhost = normalized.map(v =>
        toGhost.find(g => g.id === v.id) ? { ...v, estado: 'Ghosted' } : v
      )

      const toUpdatePriority = withGhost.filter(v => v.priority_score == null)
      for (const v of toUpdatePriority) {
        const ps = computePriorityScore(v)
        await supabase.from('jobs').update({ priority_score: ps }).eq('id', v.id)
        v.priority_score = ps
      }

      setVacantes(withGhost)
      if (withGhost.length > 0) {
        setSearchStatus({ running: false, startedAt: null })
        setSearchNotice('')
        const topCount = withGhost.filter(v => !EN_PROCESO_ESTADOS.includes(v.estado) && !HISTORIAL_ESTADOS.includes(v.estado) && safeScore(v) >= 75).length
        const recommendedCount = withGhost.filter(v => !EN_PROCESO_ESTADOS.includes(v.estado) && !HISTORIAL_ESTADOS.includes(v.estado) && safeScore(v) >= 50 && safeScore(v) < 75).length
        if (topCount > 0) setActiveTab('ðŸ”¥ Top')
        else if (recommendedCount > 0) setActiveTab('ðŸ§  Recomendadas')
      }
    } catch (err) {
      console.error('[Dashboard] loadData error:', err)
    } finally {
      loadInFlightRef.current = false
      if (!silent) setLoading(false)
    }
  }, [navigate])

  useEffect(() => { saveFilters({ location: filterLoc, role: filterRole }) }, [filterLoc, filterRole])
  useEffect(() => { loadData() }, [loadData])
  useEffect(() => {
    if (!userId) return
    setHasAutoTriggeredFirstSearch(readFirstSearchAutotriggered(userId))
    firstSearchAutoTriggerRef.current = false
  }, [userId])

  useEffect(() => {
    if (!searchRunning) return
    if (!searchStatus.startedAt || (Date.now() - searchStatus.startedAt) >= SEARCH_MAX_AGE_MS) {
      setSearchStatus({ running: false, startedAt: null })
      return
    }

    let attempts = 0
    const checkForResults = setInterval(async () => {
      attempts++
      console.log(`[Auto-refresh] Intento ${attempts}/${SEARCH_MAX_ATTEMPTS}`)
      await loadData({ silent: true })

      if (attempts >= SEARCH_MAX_ATTEMPTS) {
        clearInterval(checkForResults)
        if (vacantesCountRef.current <= searchStartCountRef.current) {
          setSearchNotice('Busqueda finalizada. No se encontraron vacantes nuevas en esta corrida.')
        }
        setSearchStatus({ running: false, startedAt: null })
      }
    }, SEARCH_POLL_INTERVAL_MS)

    return () => clearInterval(checkForResults)
  }, [loadData, searchRunning, searchStatus.startedAt])

  useEffect(() => {
    if (!userId || !searchRunning) return undefined

    const channel = supabase
      .channel(`dashboard-jobs-${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'jobs', filter: `user_id=eq.${userId}` },
        () => {
          loadData({ silent: true })
        },
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [loadData, searchRunning, userId])

  const triggerSearch = useCallback(async ({ source = 'manual', silent = false } = {}) => {
    try {
      searchStartCountRef.current = vacantesCountRef.current
      setSearchNotice('')
      setSearchStatus({ running: true, startedAt: Date.now() })
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      const { data: { user } } = await supabase.auth.getUser()

      if (!currentSession?.access_token || !user?.id) {
        throw new Error('No se encontro una sesion valida para iniciar la busqueda.')
      }

      if (source === 'auto') {
        persistFirstSearchAutotriggered(user.id, true)
        setHasAutoTriggeredFirstSearch(true)
      }

      const { error: fnError } = await supabase.functions.invoke('run-scraper', {
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
        body: { user_id: user.id, source },
      })

      if (fnError) throw fnError

      await trackEvent(user.id, source === 'auto' ? 'first_search_autostarted' : 'manual_search_started')
      if (!silent) alert('Busqueda iniciada. Tus primeras vacantes deberian aparecer en 2 a 5 minutos.')
      await loadData({ silent: true })
      return true
    } catch (err) {
      console.error('[Dashboard] search trigger error:', err)
      setSearchStatus({ running: false, startedAt: null })
      if (!silent) alert(`Error al iniciar la busqueda: ${err?.message || 'Intenta de nuevo.'}`)
      return false
    }
  }, [loadData])

  // Auto-trigger al entrar deshabilitado para evitar corridas involuntarias en login.
  // Las busquedas deben iniciar por corrida programada o accion manual autorizada.


  const handleManualSearch = async () => {
    await triggerSearch()
  }

  const canRunFirstSearchOnce = (() => {
    if (canManualSearch) return false
    if (!userId || searchRunning || (vacantes?.length || 0) > 0) return false
    if (hasAutoTriggeredFirstSearch) return false
    if (!onboardingUpdatedAt) return false
    const ts = Date.parse(onboardingUpdatedAt)
    if (Number.isNaN(ts)) return false
    return (Date.now() - ts) <= FIRST_SEARCH_WINDOW_MS
  })()

  const updateEstado = useCallback(async (id, estado, extra = {}) => {
    const patch = estado === 'Aplicado'
      ? { estado, applied_at: new Date().toISOString(), ...extra }
      : { estado, ...extra }
    setVacantes(prev => prev.map(v => v.id === id ? { ...v, ...patch } : v))
    await supabase.from('jobs').update(patch).eq('id', id)
  }, [])

  const updateScoreAdjusted = useCallback((id, score_adjusted) => {
    setVacantes(prev => prev.map(v => v.id === id ? { ...v, score_adjusted } : v))
  }, [])

  const confirmDiscard = useCallback(async (id, reason) => {
    setDiscardOpen(null)
    const discarded = vacantes.find(v => v.id === id)
    setFadingOut(prev => new Set(prev).add(id))
    setTimeout(async () => {
      await updateEstado(id, 'Descartado', { discard_reason: reason })
      trackEvent(userId, 'discard_clicked', { job_id: id, reason })
      setFadingOut(prev => { const s = new Set(prev); s.delete(id); return s })
      if (discarded) await applyDiscardMemory(discarded, reason, vacantes, updateScoreAdjusted)
    }, 350)
  }, [vacantes, updateEstado, updateScoreAdjusted, userId])

  // ── Segmentación ─────────────────────────────────────────────────────────────
  const applyFilters = (list, options = {}) => {
    const { includeHighMatch = true } = options
    let result = list
    if (filterLoc)     result = result.filter(v => getLocationText(v).toLowerCase().includes(filterLoc.toLowerCase()))
    if (filterRole)    result = result.filter(v => getRoleText(v).toLowerCase().includes(filterRole.toLowerCase()))
    if (includeHighMatch && onlyHighMatch) result = result.filter(v => safeScore(v) >= 80)
    return result
  }

  const roleOptions = Array.from(
    new Set(
      vacantes
        .map((v) => getRoleText(v).trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))

  const locationOptions = Array.from(
    new Set(
      vacantes
        .map((v) => getLocationText(v).trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))

  useEffect(() => {
    if (filterRole && !roleOptions.includes(filterRole)) {
      setFilterRole('')
    }
  }, [filterRole, roleOptions])

  useEffect(() => {
    if (filterLoc && !locationOptions.includes(filterLoc)) {
      setFilterLoc('')
    }
  }, [filterLoc, locationOptions])

  const sortFn = (a, b) => priorityMode && flagPriority
    ? (b.priority_score ?? computePriorityScore(b)) - (a.priority_score ?? computePriorityScore(a))
    : computeRanking(b) - computeRanking(a)

  const nuevos    = vacantes.filter(v => !EN_PROCESO_ESTADOS.includes(v.estado) && !HISTORIAL_ESTADOS.includes(v.estado))
  const enProceso = vacantes.filter(v => EN_PROCESO_ESTADOS.includes(v.estado))
  const historial = vacantes.filter(v => HISTORIAL_ESTADOS.includes(v.estado))
  const duplicateMap = buildDuplicateMap(vacantes)

  const getFiltered = () => {
    const base = applyFilters(nuevos).sort(sortFn)
    const proc = applyFilters(enProceso).sort(sortFn)
    const hist = applyFilters(historial, { includeHighMatch: false }).sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
    switch (activeTab) {
      case '🔥 Top':          return base.filter(v => safeScore(v) >= 75)
      case '🧠 Recomendadas': return base.filter(v => safeScore(v) < 75)
      case 'En proceso':      return proc
      case 'Historial':       return hist
      default:                return base
    }
  }

  const filtered       = getFiltered()
  const isNuevosTab    = ['🔥 Top', '🧠 Recomendadas', 'Todas'].includes(activeTab)
  const isProcesoTab   = activeTab === 'En proceso'
  const isHistorialTab = activeTab === 'Historial'

  const dbRecomendadas = estadoCounts
    ? (estadoCounts.Nuevo || 0)
    : nuevos.filter(v => safeScore(v) < 75).length
  const dbEnProceso = estadoCounts
    ? ((estadoCounts.Aplicado || 0) + (estadoCounts.Entrevista || 0) + (estadoCounts.Oferta || 0))
    : enProceso.length
  const dbHistorial = estadoCounts
    ? ((estadoCounts.Descartado || 0) + (estadoCounts.Ghosted || 0) + (estadoCounts.Rechazada || 0) + (estadoCounts.Aceptada || 0))
    : historial.length

  const counts = {
    '🔥 Top':          nuevos.filter(v => safeScore(v) >= 75).length,
    '🧠 Recomendadas': dbRecomendadas,
    'En proceso':      dbEnProceso,
    'Todas':           dbRecomendadas,
    'Historial':       dbHistorial,
  }

  const today = new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'short' })
  const nextSearchRun = getNextSearchRun(new Date(nowTick))
  const nextSearchRunLabel = nextSearchRun
    ? nextSearchRun.toLocaleString('es-MX', {
        timeZone: SEARCH_RUN_TIMEZONE,
        weekday: 'long',
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'sin programación'
  const nextSearchCountdown = formatCountdown(nextSearchRun, new Date(nowTick))
  const searchProgress = getSearchProgress(searchStatus, nowTick)

  const emptyMsg = {
    '🔥 Top':          { icon: '🎯', title: 'Sin vacantes con alto match',   hint: 'La primera corrida puede tardar de 1 a 5 minutos. Si acabas de terminar onboarding, esta pantalla se actualiza sola en cuanto lleguen resultados.' },
    '🧠 Recomendadas': { icon: '🧩', title: 'Sin vacantes recomendadas',      hint: 'Si acabas de terminar onboarding, espera 1 a 5 minutos. Si no aparecen vacantes, ajusta tus keywords en Configuración.' },
    'En proceso':      { icon: '🔄', title: 'Nada en proceso todavía',        hint: 'Marca vacantes como Aplicado para verlas aquí.' },
    'Todas':           { icon: '📭', title: 'Sin vacantes nuevas',            hint: 'La primera corrida puede tardar de 1 a 5 minutos y luego el scraper seguirá corriendo automáticamente L/M/V a las 8am CDMX.' },
    'Historial':       { icon: '📂', title: 'Historial vacío',                hint: 'Descartadas, Ghosted, Aceptadas y Rechazadas aparecerán aquí.' },
  }

  const cardProps = {
    userKeywords, duplicateMap, expanded, setExpanded,
    discardOpen, setDiscardOpen, moverAOpen, setMoverAOpen,
    fadingOut, iaConfirmed, setIaConfirmed, priorityMode,
    updateEstado, updateScoreAdjusted, confirmDiscard,
    flagIa, flagPriority,
    onTrack: (event, extra) => trackEvent(userId, event, extra),
    onAction: handleAction,
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🔍</div>
        <div style={{ color: C.muted, fontSize: '14px' }}>Cargando tu pipeline...</div>
      </div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', system-ui, sans-serif", color: C.text, overflowX: 'hidden' }}
      onClick={() => setMoverAOpen(null)}>

      {/* ── Modal upgrade ────────────────────────────────────────────────────── */}
      {showUpgrade && (
        <div onClick={() => { setShowUpgrade(false); setWaitlistDone(false) }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: C.white, borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '420px', boxSizing: 'border-box' }}>
            <div style={{ fontSize: '20px', fontWeight: '500', color: C.text, letterSpacing: '-0.5px', marginBottom: '4px' }}>Plan Premium</div>
            <div style={{ fontSize: '13px', color: C.muted, marginBottom: '20px' }}>Únete a la lista para ser el primero en acceder.</div>
            {[
              ['Keywords ilimitadas',       '3 en plan gratuito'],
              ['Vacantes ilimitadas',        '5 por día en plan gratuito'],
              ['Alertas Telegram',           'Solo en Premium'],
              ['Scoring completo detallado', 'Básico en plan gratuito'],
              ['Pipeline kanban',            'Solo en Premium'],
            ].map(([feat, note]) => (
              <div key={feat} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `0.5px solid ${C.border}`, fontSize: '13px' }}>
                <span style={{ color: C.text, fontWeight: '500' }}>✓ {feat}</span>
                <span style={{ color: C.subtle }}>{note}</span>
              </div>
            ))}
            <div style={{ fontSize: '20px', fontWeight: '500', color: C.blue, letterSpacing: '-0.5px', margin: '20px 0 16px' }}>
              Próximamente
            </div>
            {!waitlistDone ? (
              <button onClick={async () => {
                const { data: { user } } = await supabase.auth.getUser()
                await supabase.from('waitlist').upsert({ email: user.email, user_id: user.id }, { onConflict: 'email' })
                setWaitlistDone(true)
              }} style={{ width: '100%', padding: '12px', background: C.blue, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', marginBottom: '8px' }}>
                Unirme a la lista →
              </button>
            ) : (
              <div style={{ background: C.greenBg, border: `0.5px solid ${C.greenBorder}`, borderRadius: '8px', padding: '14px', textAlign: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '4px' }}>🎉</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: C.green }}>¡Ya estás en la lista!</div>
                <div style={{ fontSize: '12px', color: C.muted, marginTop: '4px' }}>Te avisamos cuando lancemos Premium.</div>
              </div>
            )}
            <button onClick={() => { setShowUpgrade(false); setWaitlistDone(false) }}
              style={{ width: '100%', padding: '10px', background: 'none', border: 'none', color: C.subtle, fontSize: '13px', cursor: 'pointer' }}>
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* ── Onboarding mínimo ─────────────────────────────────────────────────── */}
      {showOnboarding && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: C.white, borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: '400px', boxSizing: 'border-box', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🎯</div>
            <div style={{ fontSize: '18px', fontWeight: '500', color: C.text, marginBottom: '6px', letterSpacing: '-0.5px' }}>
              Bienvenido a JobPipeline
            </div>
            <div style={{ fontSize: '13px', color: C.muted, marginBottom: '24px' }}>
              Tu buscador inteligente de empleo
            </div>
            {[
              { icon: '🔍', text: 'Revisa tus recomendaciones diarias en la tab Top y Recomendadas' },
              { icon: '✓',  text: 'Aplica o descarta vacantes para que el sistema aprenda tu perfil' },
              { icon: '🔥', text: 'Activa Modo prioridad para ver primero lo más relevante' },
            ].map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', textAlign: 'left', marginBottom: '14px', padding: '12px', background: C.bg, borderRadius: '8px' }}>
                <div style={{ fontSize: '16px', flexShrink: 0 }}>{step.icon}</div>
                <div style={{ fontSize: '12px', color: C.muted, lineHeight: '1.6' }}>{step.text}</div>
              </div>
            ))}
            <button
              onClick={() => {
                localStorage.setItem('jp_onboarding_done', '1')
                setShowOnboarding(false)
              }}
              style={{ width: '100%', padding: '12px', background: C.blue, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', marginTop: '8px' }}>
              Entendido →
            </button>
          </div>
        </div>
      )}

      {/* ── Feedback 👍/👎 ────────────────────────────────────────────────────── */}
      {showFeedback && !feedbackDone && (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '12px', padding: '16px 20px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', zIndex: 300, maxWidth: '280px' }}>
          <div style={{ fontSize: '13px', fontWeight: '500', color: C.text, marginBottom: '4px' }}>¿Te están sirviendo las recomendaciones?</div>
          <div style={{ fontSize: '11px', color: C.muted, marginBottom: '12px' }}>Tu opinión mejora el sistema</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[{ emoji: '👍', label: 'Sí', value: 'yes' }, { emoji: '👎', label: 'No', value: 'no' }].map(opt => (
              <button key={opt.value} onClick={async () => {
                localStorage.setItem('jp_feedback_done', '1')
                setFeedbackDone(true)
                setShowFeedback(false)
                await trackEvent(userId, 'feedback', { value: opt.value })
              }} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: `0.5px solid ${C.border}`, background: C.bg, fontSize: '20px', cursor: 'pointer' }}>
                {opt.emoji}
              </button>
            ))}
            <button onClick={() => setShowFeedback(false)}
              style={{ padding: '8px 10px', borderRadius: '8px', border: 'none', background: 'none', fontSize: '11px', color: C.subtle, cursor: 'pointer' }}>
              Después
            </button>
          </div>
        </div>
      )}

      {showPlansModal && (
        <Planes theme={theme} session={session} isModal onClose={() => setShowPlansModal(false)} />
      )}

      {/* ── Nav ──────────────────────────────────────────────────────────────── */}
      <Header theme={theme} session={session} perfil={perfil} />

      <div style={{ maxWidth: '1180px', margin: '0 auto', padding: 'clamp(16px, 3vw, 32px)', boxSizing: 'border-box', width: '100%' }}>

        <div style={{ fontSize: '24px', fontWeight: '500', color: C.text, letterSpacing: '-0.5px', marginBottom: '2px' }}>
          Hola{perfil?.nombre ? `, ${perfil.nombre.split(' ')[0]}` : ''} 👋
        </div>
        <div style={{ fontSize: '13px', color: C.subtle, marginBottom: '20px' }}>
          {today} · Plan gratuito · {userKeywords.length || 3} keywords activas
        </div>

        <div
          style={{
            background: C.white,
            border: `0.5px solid ${C.border}`,
            borderRadius: '10px',
            padding: '10px 14px',
            marginBottom: '14px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ fontSize: '12px', fontWeight: '500', color: C.text }}>
              Próxima búsqueda automática: {nextSearchRunLabel}
            </div>
            <div style={{ fontSize: '11px', color: C.muted }}>
              {SEARCH_RUN_HUMAN_LABEL} · {nextSearchCountdown}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {canManualSearch && (
              <button
                onClick={handleManualSearch}
                disabled={loading || searchRunning}
                style={{
                  fontSize: '11px',
                  color: '#fff',
                  background: loading || searchRunning ? C.subtle : C.blue,
                  border: 'none',
                  borderRadius: '999px',
                  padding: '7px 12px',
                  cursor: loading || searchRunning ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                }}
              >
                {searchRunning ? 'Buscando...' : 'Buscar ahora'}
              </button>
            )}
            {searchRunning && (
              <span style={{ fontSize: '11px', color: C.green, background: C.greenBg, border: `0.5px solid ${C.greenBorder}`, borderRadius: '999px', padding: '4px 8px', fontWeight: '500' }}>
                Buscando ahora...
              </span>
            )}
          </div>
        </div>
        {searchProgress && (
          <div style={{ background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '10px', padding: '10px 14px', marginBottom: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: C.text }}>
                {searchProgress.phase}
              </div>
              <div style={{ fontSize: '11px', color: C.subtle }}>
                {searchProgress.pct}% aprox.
              </div>
            </div>
            <div style={{ width: '100%', height: '6px', borderRadius: '999px', background: '#eef2ff', overflow: 'hidden', marginBottom: '6px' }}>
              <div style={{ width: `${searchProgress.pct}%`, height: '100%', background: C.blue, transition: 'width 0.3s ease' }} />
            </div>
            <div style={{ fontSize: '11px', color: C.muted }}>
              {searchProgress.detail}
            </div>
          </div>
        )}

{/* First Search Button - Fallback si falló en onboarding */}
          {(!vacantes || vacantes.length === 0) && (
            <div style={{ background: C.greenBg, border: `0.5px solid ${C.greenBorder}`, borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: C.green }}>
                  {searchRunning
                    ? 'Narevya esta buscando nuevas oportunidades...'
                    : (searchNotice || 'Ya tengo tu perfil. Empiezo cuando quieras.')}
                </div>
                <div style={{ fontSize: '12px', color: C.green }}>
                  {searchRunning
                    ? 'Estoy filtrando vacantes que realmente valen la pena.'
                    : (searchNotice
                      ? 'Puedes ajustar keywords o lanzar otra busqueda cuando quieras.'
                      : 'Narevya buscara oportunidades usando tu perfil.')}
                </div>
              </div>
              {canManualSearch && (
                <button
                  onClick={handleManualSearch}
                  disabled={loading || searchRunning}
                  style={{ fontSize: '12px', color: '#fff', background: C.green, border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: '500' }}
                >
                  {searchRunning ? 'Buscando...' : 'Iniciar busqueda'}
                </button>
              )}
              {canRunFirstSearchOnce && (
                <button
                  onClick={() => triggerSearch({ source: 'auto', silent: false })}
                  disabled={loading || searchRunning}
                  style={{ fontSize: '12px', color: '#fff', background: C.green, border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: '500' }}
                >
                  {searchRunning ? 'Buscando...' : 'Iniciar primera busqueda'}
                </button>
              )}
            </div>
          )}


        {/* Banner upgrade */}
        <div style={{ background: C.blueLight, border: `0.5px solid #BFDBFE`, borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '500', color: '#1e40af', marginBottom: '2px' }}>Plan gratuito — 5 keywords, 5 vacantes por día</div>
            <div style={{ fontSize: '12px', color: '#3b82f6' }}>Actualiza a Premium para búsquedas ilimitadas y alertas Telegram</div>
          </div>
          <button onClick={() => setShowPlansModal(true)} style={{ fontSize: '11px', color: C.blue, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
            Ver planes
          </button>
        </div>

        {/* Métricas */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '22px' }}>
          {[
            { num: counts['🔥 Top'],          label: '🔥 Top',      color: C.green,  tab: '🔥 Top'          },
            { num: counts['🧠 Recomendadas'], label: '🧠 Recomen.', color: C.amber,  tab: '🧠 Recomendadas' },
            { num: counts['En proceso'],      label: 'En proceso',  color: C.blue,   tab: 'En proceso'      },
            { num: counts['Historial'],       label: 'Historial',   color: C.purple, tab: 'Historial'       },
          ].map((m) => (
            <div key={m.tab} onClick={() => setActiveTab(m.tab)}
              style={{ background: C.white, border: `1px solid ${activeTab === m.tab ? m.color : C.border}`, borderRadius: '14px', padding: '16px 18px', cursor: 'pointer', transition: 'all 0.15s', boxShadow: activeTab === m.tab ? C.shadow : 'none' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 10px 24px rgba(15,23,42,0.08)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
              <div style={{ fontSize: '34px', fontWeight: '600', color: m.color, letterSpacing: '-1px', lineHeight: '1' }}>{m.num}</div>
              <div style={{ fontSize: '11px', color: C.subtle, marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.label}</div>
            </div>
          ))}
        </div>

        {/* ── Línea 1: Tabs + filtros ───────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', marginBottom: '8px', gap: '12px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <div style={{ display: 'flex', gap: '4px', background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '10px', padding: '4px', overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch' }}>
            {TABS.map(({ id }) => {
              const isActive   = id === activeTab
              const count      = counts[id]
              const badgeBg    = id === 'Historial' ? C.purpleBg : C.blueLight
              const badgeColor = id === 'Historial' ? C.purple   : C.blue
              return (
                <button key={id} onClick={() => setActiveTab(id)}
                  style={{ padding: '7px 16px', borderRadius: '7px', fontSize: '12px', fontWeight: '500', border: 'none', cursor: 'pointer', transition: 'all 0.15s', background: isActive ? C.text : 'transparent', color: isActive ? '#fff' : C.muted, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {id}
                  {count > 0 && (
                    <span style={{ background: isActive ? 'rgba(255,255,255,0.2)' : badgeBg, color: isActive ? '#fff' : badgeColor, borderRadius: '10px', padding: '1px 7px', fontSize: '10px', fontWeight: '600' }}>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', width: isMobile ? '100%' : 'auto' }}>
            <select value={filterRole} onChange={e => setFilterRole(e.target.value)} disabled={roleOptions.length === 0}
              style={{ fontSize: '11px', padding: '6px 10px', borderRadius: '7px', border: `0.5px solid ${C.border}`, background: C.white, color: C.text, outline: 'none', width: isMobile ? '50%' : '140px', minWidth: 0, flex: isMobile ? '1 1 0' : '0 0 auto' }}>
              <option value="">{roleOptions.length === 0 ? 'Sin roles' : 'Rol…'}</option>
              {roleOptions.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
            <select value={filterLoc} onChange={e => setFilterLoc(e.target.value)} disabled={locationOptions.length === 0}
              style={{ fontSize: '11px', padding: '6px 10px', borderRadius: '7px', border: `0.5px solid ${C.border}`, background: C.white, color: C.text, outline: 'none', width: isMobile ? '50%' : '120px', minWidth: 0, flex: isMobile ? '1 1 0' : '0 0 auto' }}>
              <option value="">{locationOptions.length === 0 ? 'Sin ubicación' : 'Ubicación…'}</option>
              {locationOptions.map((location) => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
            {(filterRole || filterLoc) && (
              <button onClick={() => { setFilterRole(''); setFilterLoc('') }}
                style={{ fontSize: '11px', color: C.muted, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
            )}
          </div>
        </div>

        {/* ── Línea 2: Modo prioridad + Solo alto match ─────────────────────── */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '16px' }}>
          {flagPriority && (isNuevosTab || isProcesoTab) && (
            <button onClick={() => setPriorityMode(p => !p)}
              style={{ fontSize: '11px', padding: '6px 12px', borderRadius: '7px', border: `0.5px solid ${priorityMode ? C.red : C.border}`, background: priorityMode ? C.redBg : C.white, color: priorityMode ? C.red : C.muted, cursor: 'pointer', fontWeight: priorityMode ? '500' : '400', whiteSpace: 'nowrap' }}>
              {priorityMode ? '🔥 Modo prioridad ON' : '🔥 Modo prioridad'}
            </button>
          )}
          {(isNuevosTab || isProcesoTab) && (
            <button onClick={() => setOnlyHighMatch(p => !p)}
              style={{ fontSize: '11px', padding: '6px 12px', borderRadius: '7px', border: `0.5px solid ${onlyHighMatch ? C.green : C.border}`, background: onlyHighMatch ? C.greenBg : C.white, color: onlyHighMatch ? C.green : C.muted, cursor: 'pointer', fontWeight: onlyHighMatch ? '500' : '400', whiteSpace: 'nowrap' }}>
              {onlyHighMatch ? '✓ Alto match' : 'Solo alto match'}
            </button>
          )}
        </div>

        {/* Subtítulo Historial */}
        {isHistorialTab && historial.length > 0 && (
          <div style={{ fontSize: '12px', color: C.subtle, marginBottom: '12px' }}>
            Descartadas · Ghosted · Aceptadas · Rechazadas — ordenadas por última actualización
          </div>
        )}

        {/* Lista / empty state */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 2rem', background: C.white, border: `0.5px solid ${C.border}`, borderRadius: '12px' }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>{emptyMsg[activeTab]?.icon}</div>
            <div style={{ fontSize: '15px', fontWeight: '500', color: C.text, marginBottom: '8px' }}>{emptyMsg[activeTab]?.title}</div>
            <div style={{ fontSize: '13px', color: C.muted, lineHeight: '1.7', maxWidth: '360px', margin: '0 auto' }}>
              {emptyMsg[activeTab]?.hint}
              {activeTab === '🧠 Recomendadas' && (
                <> <span onClick={() => navigate('/configuracion')} style={{ color: C.blue, cursor: 'pointer', textDecoration: 'underline' }}>Ir a Configuración</span></>
              )}
            </div>
          </div>
        ) : (
          filtered.map(v => (
            <JobCard
              key={v.id}
              v={v}
              isNuevosTab={isNuevosTab}
              isProcesoTab={isProcesoTab}
              isHistorialTab={isHistorialTab}
              {...cardProps}
            />
          ))
        )}

      <Footer theme={theme} />
      </div>
    </div>
  )
}

